import { Injectable } from "@angular/core";
import Highcharts from "highcharts/esm/highcharts";
import Moment from "moment";

import { DatetimePipe } from "./pipes";
import { APIResults } from "./sharedinterfaces";

@Injectable()
export class ChartingService {

	public LINE_COLORS = [
		"#1FCE76",
		"#EC008C",
		"#FBAC46",
		"#66DBE1",
		"#B858DE",
		"#9805D2",
		"#690391",
	];

	public COLOR_OK = "#1FCE76"; // $health-green
	public COLOR_WARNING = "#FBAC46"; // $health-orange
	public COLOR_ALARM = "#FF6060"; // $health-red
	public COLOR_AVERAGE = "#66DBE1"; // $highlight-blue
	public COLOR_PREDICTION = "#FBAC46"; // $health-orange
	public COLOR_YELLOW = "#DBCD00"; // $highlight-yellow

	public BaseDefaults(): Highcharts.Options {
		return {
			colors: this.LINE_COLORS,
			chart: {
				zooming: {
					type: "x",
				},
				backgroundColor: "transparent",
				plotBackgroundColor: "transparent",
				ignoreHiddenSeries: true,
				spacing: [15, 15, 15, 15],
				style: {
					fontFamily: "'Source Sans Pro', sans-serif",
					color: "#cccccc",
				},
			},
			series: [],
			credits: { enabled: false },
			title: {
				text: undefined,
				style: {
					color: "#cccccc",
				},
			},
			exporting: {
				enabled: true
			},
			xAxis: {
				gridLineColor: "#4d4d4d",
				title: {
					style: { color: "#cccccc" },
				},
				labels: {
					style: {
						color: "#cccccc",
					},
				},
			},
			yAxis: {
				title: {
					style: { color: "#cccccc" },
				},
				gridLineColor: "#4d4d4d",
				labels: {
					style: {
						color: "#cccccc",
					},
				},
			},
			legend: {
				enabled: true,
				margin: 6,
				padding: 3,
				y: 6,
				itemStyle: {
					color: "#cccccc",
				},
			},
			plotOptions: {
				area: {
					lineWidth: 1,
					marker: { enabled: true, radius: 3, fillColor: this.COLOR_OK },
					shadow: false,
					threshold: 0
				},
				line: {
					states: { hover: { lineWidth: 2 } },
				},
				series: {
					animation: {
						duration: 500,
						easing: "swing",
					},
				}
			},
			tooltip: {
				borderRadius: 15,
			},

		};
	}

	public FlightTimeDefaults(title: string, detail: string, xAxisTitle: string, xAxisIsDate: boolean, series: Highcharts.SeriesLineOptions[], dates: number[][] | number[], pointdetails: (number) => string, units: string, tzoffset: number, warning?: number, alarm?: number, fixed?: number): Highcharts.Options {
		const opt = this.BaseDefaults();
		if (opt.tooltip) {
			opt.tooltip.shared = false;
			opt.tooltip.formatter = function () {
				if (xAxisIsDate) {
					const x = Number(this.x);
					const pretty = new DatetimePipe().transform(Moment.unix(x / 1000), "offset", tzoffset);
					let tip = "<b>" + pretty + "</b>";
					// Highcharts typedefs are wrong here
					let point = (this as any).point;
					if (!point) {
						point = (this.points[0] as any).point;
					}
					if (pointdetails) {
						tip += "<br/>" + pointdetails(point);
					}
					const y = (fixed !== undefined ? point.y.toFixed(fixed) : point.y);
					tip += "<br/><span style=\"color: " + this.color + "\">\u25CF</span> " + point.series.name + ": " + y;
					const optunits = point.series.options as SeriesWithUnits;
					if (optunits.units) {
						tip += " " + optunits.units;
					}
					return tip;
				} else {
					const x = Number(this.x);
					let tip = "<b>" + x.toFixed(2) + "</b>";
					// Highcharts typedefs are wrong here
					let point = (this as any).point;
					if (!point) {
						point = (this.points[0] as any).point;
					}
					const pointindex = point as PointWithIndex;
					let seriesdates: number[] = dates as number[];
					// Dates can be passed as either an array of dates (if all series have the same indices) or an array of date arrays by series
					if (dates && dates.length && Array.isArray(dates[0])) {
						const seriesindex = pointindex.series as SeriesWithIndex;
						seriesdates = dates[seriesindex.index] as number[];
					}
					if (seriesdates.length >= pointindex.index && !this.series.name.startsWith("Predicted")) {
						const pretty = new DatetimePipe().transform(Moment.unix(seriesdates[pointindex.index]), "offset", tzoffset);
						tip += "<br/>" + pretty;
					}
					if (pointdetails) {
						tip += "<br/>" + pointdetails(point);
					}
					const y = (fixed !== undefined ? point.y.toFixed(fixed) : point.y);
					tip += "<br/><span style=\"color: " + this.color + "\">\u25CF</span> " + point.series.name + ": " + y;
					const optunits = point.series.options as SeriesWithUnits;
					if (optunits.units) {
						tip += " " + optunits.units;
					}
					return tip;
				}
			};
		}

		if (opt.title) {
			opt.title.text = title;
			if (detail) {
				opt.title.useHTML = true;
				opt.title.text += " <i class='icon-help-circled' title='" + detail + "'></i>";
			}
		}

		opt.series = series;
		if (opt.legend) {
			if (series.length > 1) {
				opt.legend.enabled = true;
			} else {
				opt.legend.enabled = false;
			}
		}

		if (!Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			if (xAxisIsDate) {
				xaxis.type = "datetime";
			}
			if (xaxis.title) {
				xaxis.title.text = xAxisTitle;
			}
		}

		if (!Array.isArray(opt.yAxis)) {
			const yaxis: Highcharts.AxisOptions = opt.yAxis;
			if (yaxis.title) {
				yaxis.title.text = units;
			}

			yaxis.plotLines = [];
			if (warning) {
				yaxis.plotLines.push({ label: { useHTML: true, text: "Warning <i class='icon-help-circled' title='Component is abnormal: Investigate, Monitor, and Plan'></i>", style: { color: this.COLOR_WARNING }, y: 12 }, value: warning, width: 2, color: this.COLOR_WARNING, dashStyle: "Dash" });
				yaxis.softMax = warning;
			}
			if (alarm) {
				yaxis.plotLines.push({ label: { useHTML: true, text: "Alarm <i class='icon-help-circled' title='Component is unreliable: Confirm and Act'></i>", style: { color: this.COLOR_ALARM }, y: 12 }, value: alarm, width: 2, color: this.COLOR_ALARM, dashStyle: "Dash" });
				yaxis.softMax = alarm;
			}
		}

		return opt;
	}

	public FlightTimeWithMaint(title: string, detail: string, xAxisTitle: string, xAxisIsDate: boolean, series: Highcharts.SeriesLineOptions[], dates: number[] | number[][], pointdetails: (number) => string, units: string, tzoffset: number, warning?: number, alarm?: number, maintresult?: APIResults.MaintenanceHistory[], lru?: number, showRegimes = false, fixed?: number): Highcharts.Options {
		const opt = this.FlightTimeDefaults(title, detail, xAxisTitle, xAxisIsDate, series, dates, pointdetails, units, tzoffset, warning, alarm, fixed);
		if (!Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			// This links the zoom of visible charts but goes way too slow with several charts
			// xaxis.events = {
			// 	afterSetExtremes: function (event) {
			// 		Highcharts.charts.forEach(chart => {
			// 			if (chart && chart.index !== (<any>event.target).chart.index) {
			// 				chart.xAxis[0].setExtremes(event.min, event.max);
			// 			}
			// 		})
			// 	}
			// }
			xaxis.plotLines = [];

			if (maintresult) {
				let seriesdates: number[] = dates as number[];
				if (dates && dates.length && Array.isArray(dates[0])) {
					seriesdates = dates[0] as number[];
				}

				const stripes: ChartStripe[] = [];
				for (const maint of maintresult) {
					// Only include maintenance events for matching LRUs
					if ((maint.lru === null) || (lru === maint.lru)) {
						const date = new Date(maint.ts);
						const datenum = date.getTime() / 1000;
						let note = maint.user_note;
						if (note.length > 25) {
							note = note.substr(0, 20) + "...";
						}
						if (!xAxisIsDate) {
							// Figure out what flight-hour each maintenance event is
							for (let i = 0; i < seriesdates.length; i++) {
								if (seriesdates[i] > datenum) {
									if (stripes.length && stripes[stripes.length - 1].axisvalue === series[0].data[i][0]) {
										// Skip - don't put two stripes at the same time
									} else {
										stripes.push({
											axisvalue: series[0].data[i][0],
											title: date.getUTCFullYear() + "-" + (date.getUTCMonth() + 1) + "-" + date.getUTCDate() + ": " + note,
										});
									}
									break;
								}
							}
						} else {
							// Just push maintenance events using dates
							stripes.push({
								axisvalue: datenum * 1000,
								title: date.getUTCFullYear() + "-" + (date.getUTCMonth() + 1) + "-" + date.getUTCDate() + ": " + note,
							});
						}
					}
				}

				for (const stripe of stripes) {
					xaxis.plotLines.push({
						label: { text: stripe.title, style: { color: this.COLOR_YELLOW }, y: 12 },
						value: stripe.axisvalue,
						width: 2,
						color: this.COLOR_YELLOW,
						dashStyle: "LongDash",
						zIndex: 3,
					});
				}
			}
		}
		if (!Array.isArray(opt.yAxis) && showRegimes) {
			const yaxis: Highcharts.AxisOptions = opt.yAxis;
			opt.yAxis = [yaxis, {
				visible: false,
			}];
			const regimeSeries = series[series.length - 1];
			regimeSeries.yAxis = 1;
			regimeSeries.visible = true;
			regimeSeries.showInLegend = true;
		}

		return opt;
	}

	public RevolutionDefaults(title: string, stats: APIResults.DetailStatistics, rpm: number, data: number[], units: string): Highcharts.Options {
		const opt = this.BaseDefaults();

		if (opt.title) {
			opt.title.text = title;
			if (!isNaN(stats.rms)) {
				opt.title.text += " | RMS: " + stats.rms.toFixed(3);
			}
			if (!isNaN(stats.kurtosis)) {
				opt.title.text += ", Kurtosis: " + stats.kurtosis.toFixed(3);
			}
			if (!isNaN(stats.crestfactor)) {
				opt.title.text += ", Crest Factor: " + stats.crestfactor.toFixed(3);
			}
			if (!isNaN(stats.peaktopeak)) {
				opt.title.text += ", Peak To Peak: " + stats.peaktopeak.toFixed(3);
			}
			if (!isNaN(rpm)) {
				opt.title.text += ", RPM: " + rpm.toFixed(3);
			}
		}

		if (!Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			if (xaxis.title) {
				xaxis.title.text = "1 Rev";
			}
		}

		if (!Array.isArray(opt.yAxis)) {
			const yaxis: Highcharts.AxisOptions = opt.yAxis;
			if (yaxis.title) {
				yaxis.title.text = units;
			}
		}

		if (opt.legend) {
			opt.legend.enabled = false;
		}
		if (opt.tooltip) {
			opt.tooltip.shared = false;
		}

		opt.series = [{ type: "line", name: title, data: data }];
		// opt.tooltip.formatter = function() {
		// 	return this.x;
		// }
		return opt;
	}

	public SpectrumDefaults(title: string, yunits: string, series: Highcharts.SeriesLineOptions[], useorder: boolean, stripes?: ChartStripe[]): Highcharts.Options {
		const opt = this.BaseDefaults();

		if (opt.title) {
			opt.title.text = title;
		}

		if (!Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			if (xaxis.title) {
				if (useorder) {
					xaxis.title.text = "Order";
				} else {
					xaxis.title.text = "Freq (Hz)";
				}
			}

			xaxis.plotLines = [];
			if (stripes) {
				for (const stripe of stripes) {
					xaxis.plotLines.push({ label: { text: stripe.title }, value: stripe.axisvalue, width: 2, color: "#666666" });
				}
			}
		}

		if (!Array.isArray(opt.yAxis)) {
			const yaxis: Highcharts.AxisOptions = opt.yAxis;
			if (yaxis.title) {
				yaxis.title.text = yunits;
			}
		}

		if (opt.legend) {
			opt.series = series;
			if (series.length > 1) {
				opt.legend.enabled = true;
			} else {
				opt.legend.enabled = false;
			}
		}
		if (opt.tooltip) {
			opt.tooltip.shared = false;
		}
		// opt.tooltip.formatter = function() {
		// 	return this.x;
		// }
		return opt;
	}

	public RTBDefaults(): Highcharts.Options {
		const opt = this.BaseDefaults();

		if (opt.chart) {
			opt.chart.polar = true;
			opt.chart.zooming = {
				type: undefined,
			};
		}

		opt.pane = {
			startAngle: 0,
			endAngle: 360
		};

		if (!Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			xaxis.tickInterval = 30;
			xaxis.min = 0;
			xaxis.max = 360;
			if (xaxis.labels) {
				xaxis.labels.formatter = function (this: any) {
					return this.value + "°";
				};
			}
		}

		if (!Array.isArray(opt.yAxis)) {
			const yaxis: Highcharts.AxisOptions = opt.yAxis;
			yaxis.min = 0;
		}

		if (opt.tooltip) {
			opt.tooltip.headerFormat = "<span style='font-size: 10px'>{series.name}</span><br/>";
			opt.tooltip.pointFormatter = function (this: any) {
				let prefix = "Magnitude";
				switch (this.name) {
					case "Average":
						prefix = "Average Magnitude";
						break;
					case "Prediction":
						prefix = "Predicted Magnitude";
						break;
					case "Before Average":
						prefix = "Before Operation<br/>Average Magnitude";
						break;
					case "After Average":
						prefix = "After Operation<br/>Average Magnitude";
						break;
				}
				return prefix + ": <b>" + this.y.toFixed(2) + " IPS</b><br/>Direction: <b>" + this.x.toFixed(2) + "\u00B0</b><br/>";
			};
		}

		return opt;
	}

	public RTBScatterDefaults(series: Highcharts.SeriesScatterOptions[]): Highcharts.Options {
		const opt = this.RTBDefaults();
		opt.series = series;
		return opt;
	}

	public RTBLineDefaults(series: Highcharts.SeriesScatterOptions[], tzoffset: number): Highcharts.Options {
		const opt = this.RTBDefaults();

		// opt.plotOptions.scatter = { lineWidth: 1 };

		if (opt.tooltip) {
			opt.tooltip.headerFormat = "<span style='font-size: 10px'>{series.name}</span><br/>";
			opt.tooltip.pointFormatter = function (this: any) {
				const pretty = new DatetimePipe().transform(Moment.unix(this.time), "offset", tzoffset);
				let tip = "<b>" + pretty + "</b>";
				tip += "<br/>Avg Magnitude: <b>" + this.y.toFixed(2) + " IPS</b><br/>Direction: <b>" + this.x.toFixed(2) + "\u00B0</b><br/>";
				return tip;
			};
		}

		opt.series = series;
		return opt;
	}

	public RTBHistoryTimelineDefaults(series: Highcharts.SeriesLineOptions[], units: string, tzoffset: number, adjustments: APIResults.AdjustmentHistory[], zoomfunc: (any) => void, callercontext: any): Highcharts.Options {
		const opt = this.FlightTimeDefaults("", "", "Time", true, series, undefined, undefined, units, tzoffset);

		if (adjustments && adjustments.length && !Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			xaxis.plotLines = [];
			for (const adjustment of adjustments) {
				const unix = Moment(adjustment.operation_start_time).unix();
				const pretty = new DatetimePipe().transform(Moment(adjustment.operation_start_time), "offsetshort", tzoffset);
				xaxis.plotLines.push({
					label: { text: pretty, style: { color: this.COLOR_YELLOW }, y: 12 },
					value: unix * 1000,
					width: 2,
					color: this.COLOR_YELLOW,
					dashStyle: "LongDash",
					zIndex: 3,
				});
			}
			if (zoomfunc) {
				xaxis.events = {
					setExtremes: zoomfunc.bind(callercontext),
				};
			}
		}

		return opt;
	}

	public RTBTrackDefaults(series: Highcharts.SeriesScatterOptions[], blades: string[], trackUnits: string): Highcharts.Options {
		const opt = this.BaseDefaults();

		if (!Array.isArray(opt.xAxis)) {
			const xaxis: Highcharts.AxisOptions = opt.xAxis;
			xaxis.type = "category";
			xaxis.categories = blades;
			xaxis.tickInterval = 1;
			xaxis.gridLineWidth = 1;
			xaxis.tickmarkPlacement = "on";
		}

		if (!Array.isArray(opt.yAxis)) {
			const yaxis: Highcharts.AxisOptions = opt.yAxis;
			yaxis.title.text = "Height (" + trackUnits + ")";
		}

		opt.series = series;

		if (opt.tooltip) {
			opt.tooltip.headerFormat = "<span style='font-size: 10px'>{series.name}</span><br/>";
			opt.tooltip.pointFormatter = function (this: any) {
				if (Object.prototype.hasOwnProperty.call(this, "leadlag")) {
					return "Average Track: <b>" + this.y.toFixed(2) + trackUnits + "</b><br/>Lead/lag: <b>" + this.leadlag.toFixed(2) + "\u00B0</b><br/>";
				} else {
					return "Predicted Track: <b>" + this.y.toFixed(2) + trackUnits + "</b><br/>";
				}
			};
		}

		return opt;
	}
	public RTBTrackCoeff(series: Highcharts.SeriesScatterOptions[], blades: string[]): Highcharts.Options {
		// Track coefficients are always displayed in mm
		const opt = this.RTBTrackDefaults(series, blades, "mm");
		if (opt.tooltip) {
			opt.tooltip.headerFormat = "<span style='font-size: 10px'>{series.name}</span><br/>";
			opt.tooltip.pointFormatter = function (this: any) {
				let prefix = "";
				if (this.name === "Before Average") {
					prefix = "Before Operation<br/>";
				} else if (this.name === "After Average") {
					prefix = "After Operation<br/>";
				}
				return prefix + "Average Track: <b>" + this.y.toFixed(2) + "mm</b><br/>Lead/lag: <b>" + this.leadlag.toFixed(2) + "\u00B0</b><br/>";
			};
		}
		return opt;
	}

	public BoxPlotDefaults(name: string, categories: string[], assetStats: APIResults.BoxPlotStats[], assetOutliers: number[][], warning?: number, alarm?: number): Highcharts.Options {
		const opt = this.BaseDefaults();

		if (opt.chart) {
			opt.chart.type = "boxplot";
		}

		const seriesopts = opt.plotOptions.series as Highcharts.SeriesBoxplotOptions;
		seriesopts.maxPointWidth = 80;

		if (!Array.isArray(opt.xAxis)) {
			opt.xAxis.categories = categories;
			// opt.xAxis.tickInterval = 30;
			// opt.xAxis.min = 0;
			// opt.xAxis.max = 360;
		}

		if (!Array.isArray(opt.yAxis)) {
			opt.yAxis.title.text = "HI";

			if (!opt.yAxis.plotLines) {
				opt.yAxis.plotLines = [];
			}

			if (warning) {
				opt.yAxis.plotLines.push({ label: { text: "Warning", style: { color: this.COLOR_WARNING }, y: 12 }, value: warning, width: 2, color: this.COLOR_WARNING, dashStyle: "Dash" });
			}
			if (alarm) {
				opt.yAxis.plotLines.push({ label: { text: "Alarm", style: { color: this.COLOR_ALARM } }, value: alarm, width: 2, color: this.COLOR_ALARM, dashStyle: "Dash" });
			}
			// opt.yAxis.min = 0;
		}

		const boxplotSeries: Highcharts.SeriesBoxplotOptions = {
			name: name,
			type: "boxplot",
			data: assetStats,
		};

		const outliersGrouped: any[] = [];
		for (let col = 0; col < assetOutliers.length; col++) {
			for (const point of assetOutliers[col]) {
				outliersGrouped.push([col, point]);
			}
		}
		const outlierSeries: Highcharts.SeriesScatterOptions = {
			name: "Outlier",
			type: "scatter",
			data: outliersGrouped,
			tooltip: {
				pointFormatter: function (this: any) {
					return "Revised HI: <b>" + this.y.toFixed(3) + "</b>";
				},
			},
		};

		opt.series = [boxplotSeries, outlierSeries];

		return opt;
	}

	public GetExceedanceBands(exceedances: APIResults.Exceedance[]): Highcharts.XAxisPlotBandsOptions[] {
		// Include exceedance lines colored by severity
		// Only show an exceedance label every 5% of the flight time
		const exceedanceBands: Highcharts.XAxisPlotBandsOptions[] = [];
		for (const ex of exceedances) {
			const label: Highcharts.XAxisPlotBandsLabelOptions = {
				text: ex.name,
				align: "left",
				rotation: 90,
				x: -14,
				y: 4,
				style: {
					color: ex.severity === 0 ? this.COLOR_WARNING : this.COLOR_ALARM,
					textShadow: "#000 1px 1px 2px",
				},
			}
			exceedanceBands.push({
				from: ex.timestamp.valueOf(),
				to: ex.timestamp.valueOf() + ex.duration * 1000,
				color: ex.severity === 0 ? this.COLOR_WARNING + "80" : this.COLOR_ALARM + "80",
				zIndex: 2,
				label,
			});
		}
		return exceedanceBands;
	}
}

export interface ChartStripe {
	title: string;
	axisvalue: number;
}

interface SeriesWithUnits extends Highcharts.SeriesLineOptions {
	units: string;
}

interface PointWithIndex extends Highcharts.Point {
	index: number;
}

interface SeriesWithIndex extends Highcharts.Series {
	index: number;
}
